"use client"

import { useState } from "react"
import Card from "../modules/Card/Card"
import Tabs from "../modules/Tabs/Tabs"
import PieChart from "../modules/Charts/PieChart"
import BarChart from "../modules/Charts/BarChart"
import styles from "../../../pages/inventory/InventoryDashboardPage.module.css"
import Spinner from "../../spinner"

/**
 * DashboardMetricsCard
 *
 * Props:
 *  - chartData: object with named chart arrays (same as before)
 *  - headerButtons: array (same as before)
 *  - mainTabs: array (same as before)
 *  - subTabs: object (same as before)
 *  - loading: boolean (NEW) -> when true, show overlay loader for the whole metrics card
 *
 * Only addition: `loading` prop and a small overlay spinner. Everything else unchanged.
 */

const DashboardMetricsCard = ({ chartData, headerButtons, mainTabs, subTabs, loading = false }) => {
  const [activeMainTab, setActiveMainTab] = useState("refurbishment")
  const [activeSubTab, setActiveSubTab] = useState("refurbished-devices")

  const handleMainTabChange = (tabId) => {
    setActiveMainTab(tabId)
    const firstSubTab = subTabs[tabId][0].id
    setActiveSubTab(firstSubTab)
  }

  const handleSubTabChange = (tabId) => {
    setActiveSubTab(tabId)
  }

  const renderChart = () => {
    switch (activeSubTab) {
      case "refurbished-devices":
        return (
          <Card title="Refurbished Devices by Type">
            <PieChart data={chartData.refurbishedDevices} size="medium" showLegend={true} showLabels={true} />
          </Card>
        )

      case "refurbishment-status":
        return (
          <Card title="Refurbishment Status">
            <PieChart data={chartData.refurbishmentStatus} size="medium" showLegend={true} showLabels={true} />
          </Card>
        )

      case "warehouse-inventory":
        return (
          <Card title="Warehouse Inventory Status">
            <PieChart data={chartData.warehouseInventory} size="medium" showLegend={true} showLabels={true} />
          </Card>
        )

      case "device-distribution":
        return (
          <Card title="Device Distribution by Client">
            <PieChart data={chartData.deviceDistribution} size="medium" showLegend={true} showLabels={true} />
          </Card>
        )

      case "device-purchase-warranty":
        return (
          <Card title="Device Purchase/Warranty Overview">
            <PieChart data={chartData.devicePurchaseWarranty} size="medium" showLegend={true} showLabels={true} />
          </Card>
        )

      case "warranties-products":
        return (
          <Card title="Warranties on Products">
            <PieChart data={chartData.warrantiesOnProducts} size="medium" showLegend={true} showLabels={true} />
          </Card>
        )

      case "product-type":
        return (
          <Card title="Product Type Distribution">
            <PieChart data={chartData.productType} size="medium" showLegend={true} showLabels={true} />
          </Card>
        )

      case "claims-summary":
        return (
          <Card title="Claims Summary by Care Manager">
            <BarChart
              data={chartData.claimsSummary}
              xAxisKey="name"
              yAxisKey="value"
              colors={["#10164d"]}
              height={350}
            />
          </Card>
        )

      default:
        return (
          <Card title="Coming Soon">
            <div className={styles.coming_soon}>
              <h3>Coming Soon</h3>
              <p>This section is under development</p>
            </div>
          </Card>
        )
    }
  }

  // Inline spinner + overlay styles (keeps things self-contained)
  const overlayStyle = {
    // position: "absolute",
    // inset: 0,
    // background: "rgba(255,255,255,0.7)",
    // // backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 50,
    padding: 20,
  }

  const spinnerBoxStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 10,
    color: "#333",
  }

  const spinnerStyle = {
    width: 48,
    height: 48,
    borderRadius: "50%",
    border: "4px solid rgba(0,0,0,0.08)",
    borderTopColor: "#0b5fff",
    animation: "dash-rotate 1s linear infinite",
  }

  // Add the keyframes via a small style tag so we don't require external CSS changes
  const spinnerKeyframes = `
    @keyframes dash-rotate {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `

  return (
    <div className={styles.section} style={{ position: "relative" }}>
      {/* inject keyframes */}
      <style>{spinnerKeyframes}</style>

      <Card title="Inventory Metrics" headerButtons={headerButtons} className={styles.metrics_card}>
        <div className={styles.main_tabs_container}>
          <Tabs
            tabs={mainTabs}
            activeTab={activeMainTab}
            onChange={handleMainTabChange}
            variant="default"
            size="medium"
          />
        </div>

        <div className={styles.sub_tabs_container}>
          <Tabs
            tabs={subTabs[activeMainTab]}
            activeTab={activeSubTab}
            onChange={handleSubTabChange}
            variant="secondary"
            size="small"
          />
        </div>

        <div className={styles.chart_content}>{renderChart()}</div>
      </Card>

      {/* Loading overlay */}
      {loading && (
        <div style={overlayStyle} role="status" aria-live="polite" aria-label="Loading metrics">
         
          <Spinner></Spinner>
       
        </div>
      )}
    </div>
  )
}

export default DashboardMetricsCard
